import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { StageSelect, StageBadge } from "../components/Stage";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Trash, FloppyDisk, Plus, EnvelopeSimple, PaperPlaneTilt, MapPin, Phone, LinkedinLogo, Globe, Archive, UsersThree, Note, Clock, Lightning, Star, X, CalendarCheck, CheckSquare, CheckCircle, VideoCamera } from "@phosphor-icons/react";
import NextBestAction from "../components/NextBestAction";
import RelationshipScore from "../components/RelationshipScore";
import AISuggestions from "../components/AISuggestions";
import DraftMessageModal from "../components/DraftMessageModal";
import CommPrefs from "../components/CommPrefs";
import VisibilityPicker from "../components/VisibilityPicker";

const RELATIONSHIP_TYPES = [
  "", "prospect", "client", "referral_partner", "business_partner", "vendor",
  "investor", "agent", "broker", "colleague", "friend", "other",
];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const NOTE_TYPES = ["general", "conversation", "meeting", "phone_call", "email", "opportunity", "referral", "personal"];

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});
  const [interaction, setInteraction] = useState({ type: "note", notes: "" });
  const [fup, setFup] = useState({ type: "message", due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10), notes: "" });
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  // Phase 1 additions
  const [tab, setTab] = useState("timeline");
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState({ note: "", note_type: "general" });
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  // Phase 2 — meetings + tasks
  const [meetings, setMeetings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: "", start_time: "", end_time: "", location: "", meeting_url: "", description: "",
  });
  const [taskForm, setTaskForm] = useState({ title: "", due_at: "", priority: "normal" });
  // Phase 3 — campaign enrollment
  const [showCampaignPicker, setShowCampaignPicker] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [contactEnrollments, setContactEnrollments] = useState([]);
  // Phase 4 — AI draft modal
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftDefaultChannel, setDraftDefaultChannel] = useState("email");
  const [suggestionsReloadKey, setSuggestionsReloadKey] = useState(0);

  const load = () => api.get(`/contacts/${id}`).then((r) => { setData(r.data); setEdit(r.data.contact); });
  const loadTimeline = () => api.get(`/contacts/${id}/timeline`).then((r) => setTimeline(r.data.timeline || []));
  const loadNotes = () => api.get(`/contacts/${id}/notes`).then((r) => setNotes(r.data.notes || []));
  const loadTags = () => api.get(`/tags`).then((r) => setAllTags(r.data.tags || []));
  const loadMeetings = () => api.get(`/calendar/events?contact_id=${id}`).then((r) => setMeetings(r.data.events || []));
  const loadTasks = () => api.get(`/tasks?contact_id=${id}`).then((r) => setTasks(r.data.tasks || []));
  const loadCampaignData = () => Promise.all([
    api.get("/automation/campaigns").then((r) => setAvailableCampaigns((r.data.campaigns || []).filter((c) => c.status === "active"))),
    api.get(`/automation/enrollments?contact_id=${id}`).then((r) => setContactEnrollments(r.data.enrollments || [])),
  ]);

  useEffect(() => {
    load();
    loadTimeline();
    loadNotes();
    loadTags();
    loadMeetings();
    loadTasks();
    loadCampaignData();
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
    api.get("/events").then((r) => setEvents(r.data));
  }, [id]);

  // Stage-change triggers auto campaigns
  useEffect(() => {
    if (data && edit.stage && edit.stage !== data.contact.stage) {
      api.put(`/contacts/${id}/stage`, { stage: edit.stage }).then(() => {
        toast.success(`Moved to ${edit.stage} · auto campaigns triggered`);
        load();
      }).catch(() => {});
    }
  }, [edit.stage]);

  if (!data) return <div className="p-10 text-[#C7DDE5]">Loading...</div>;
  const c = data.contact;

  const save = async () => {
    try { await api.put(`/contacts/${id}`, edit); toast.success("Saved"); load(); }
    catch { toast.error("Save failed"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete contact?")) return;
    await api.delete(`/contacts/${id}`); toast.success("Deleted"); nav("/contacts");
  };
  const addInteraction = async () => {
    if (!interaction.notes.trim()) return;
    await api.post("/interactions", { contact_id: id, ...interaction });
    setInteraction({ type: "note", notes: "" });
    load();
    loadTimeline();
  };

  // Phase 1 — notes
  const addNote = async () => {
    if (!newNote.note.trim()) return;
    try {
      await api.post(`/contacts/${id}/notes`, newNote);
      setNewNote({ note: "", note_type: "general" });
      loadNotes();
      loadTimeline();
      toast.success("Note added");
    } catch { toast.error("Could not add note"); }
  };
  const deleteNote = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/contacts/${id}/notes/${nid}`);
    loadNotes();
  };

  // Phase 1 — tags
  const addTag = async () => {
    const name = tagInput.trim();
    if (!name) return;
    try {
      await api.post(`/contacts/${id}/tags`, { name });
      setTagInput("");
      load();
      loadTags();
      toast.success(`Tagged with "${name}"`);
    } catch { toast.error("Could not add tag"); }
  };
  const removeTag = async (tagName) => {
    const tag = allTags.find((t) => t.name === tagName);
    if (tag) {
      await api.delete(`/contacts/${id}/tags/${tag.id}`);
    } else {
      // Fallback — remove from legacy array via PUT
      const nextTags = (data.contact.tags || []).filter((t) => t !== tagName);
      await api.put(`/contacts/${id}`, { tags: nextTags });
    }
    load();
  };

  // Phase 1 — archive
  const archiveContact = async () => {
    if (!window.confirm("Archive this contact? They will be hidden from the main list but data is kept.")) return;
    await api.post(`/contacts/${id}/archive`, { archive: true });
    toast.success("Archived");
    nav("/contacts");
  };

  // Phase 2 — meetings
  const createMeeting = async () => {
    if (!meetingForm.title || !meetingForm.start_time || !meetingForm.end_time) {
      return toast.error("Title, start and end are required");
    }
    try {
      const r = await api.post("/calendar/events", {
        contact_id: id, ...meetingForm,
        start_time: new Date(meetingForm.start_time).toISOString(),
        end_time: new Date(meetingForm.end_time).toISOString(),
      });
      if (r.data.provider_status === "local_only") {
        toast.message("Meeting saved locally", { description: "Connect Google / Outlook calendar to sync externally." });
      } else {
        toast.success("Meeting scheduled");
      }
      setMeetingForm({ title: "", start_time: "", end_time: "", location: "", meeting_url: "", description: "" });
      setShowMeetingForm(false);
      loadMeetings();
      loadTimeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to schedule");
    }
  };
  const completeMeeting = async (m) => {
    const notes = window.prompt("Meeting outcome notes (optional):", "");
    try {
      await api.put(`/calendar/events/${m.id}`, { status: "completed", outcome_notes: notes || null });
      toast.success("Meeting completed");
      loadMeetings();
      loadTimeline();
    } catch { toast.error("Failed"); }
  };
  const cancelMeeting = async (m) => {
    await api.put(`/calendar/events/${m.id}`, { status: "cancelled" });
    loadMeetings();
    loadTimeline();
  };

  // Phase 2 — tasks
  const createTask = async () => {
    if (!taskForm.title.trim()) return;
    try {
      await api.post("/tasks", { ...taskForm, contact_id: id, due_at: taskForm.due_at || null });
      setTaskForm({ title: "", due_at: "", priority: "normal" });
      setShowTaskForm(false);
      toast.success("Task created");
      loadTasks();
      loadTimeline();
    } catch { toast.error("Failed"); }
  };
  const toggleTaskComplete = async (t) => {
    await api.put(`/tasks/${t.id}`, { status: t.status === "completed" ? "pending" : "completed" });
    loadTasks();
    loadTimeline();
  };

  // Phase 3 — campaign enrollment
  const enrollInCampaign = async (campaign_id) => {
    try {
      const r = await api.post("/automation/enrollments", { campaign_id, contact_ids: [id] });
      if (r.data.enrolled?.length) {
        toast.success("Added to campaign");
      } else if (r.data.skipped?.length) {
        toast.error(`Skipped: ${r.data.skipped[0].reason}`);
      }
      setShowCampaignPicker(false);
      loadCampaignData();
      loadTimeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to enroll");
    }
  };
  const pauseEnrollment = async (eid) => {
    await api.post(`/automation/enrollments/${eid}/pause`);
    loadCampaignData();
  };
  const resumeEnrollment = async (eid) => {
    await api.post(`/automation/enrollments/${eid}/resume`);
    loadCampaignData();
  };
  const removeEnrollment = async (eid) => {
    if (!window.confirm("Remove from campaign?")) return;
    await api.delete(`/automation/enrollments/${eid}`);
    loadCampaignData();
    loadTimeline();
  };
  const addFollowup = async () => {
    await api.post("/followups", { contact_id: id, ...fup, due_date: new Date(fup.due_date).toISOString() });
    setFup({ ...fup, notes: "" });
    toast.success("Reminder set");
    load();
  };
  const applyCampaign = async (campId) => {
    await api.post(`/campaigns/${campId}/apply/${id}`);
    toast.success("Campaign applied");
    load();
  };
  const fillFromTemplate = async (t) => {
    const eventName = c.event?.name || "";
    const body = t.body
      .replaceAll("{{name}}", c.name)
      .replaceAll("{{company}}", c.company || "")
      .replaceAll("{{event}}", eventName)
      .replaceAll("{{me}}", "");
    const subject = (t.subject || "").replaceAll("{{name}}", c.name).replaceAll("{{event}}", eventName);
    // try Resend first if email available + configured
    if (c.email) {
      try {
        const st = await api.get("/integrations/status");
        if (st.data.email_configured) {
          await api.post("/send/email", { contact_id: id, subject: subject || `From ${c.name}`, body });
          toast.success("Email sent via Resend");
          load();
          return;
        }
      } catch { /* integration status unknown — fall through to mailto */ }
      window.open(`mailto:${c.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    } else {
      navigator.clipboard.writeText(body);
      toast.success("Copied to clipboard");
    }
  };

  const sendEmail = async () => {
    if (!c.email) { toast.error("No email on file"); return; }
    const body = window.prompt("Message body:", `Hi ${c.name},\n\n`);
    if (body === null) return;
    const subject = window.prompt("Subject:", `Following up · ${c.name}`) || `Following up · ${c.name}`;
    try {
      const st = await api.get("/integrations/status");
      if (!st.data.email_configured) {
        window.open(`mailto:${c.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        return;
      }
      await api.post("/send/email", { contact_id: id, subject, body });
      toast.success("Email sent via Resend");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Email failed"); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="contact-detail-page">
      <Toaster theme="dark" />
      <Link to="/contacts" className="overline inline-flex items-center gap-2 mb-6 hover:text-white"><ArrowLeft weight="bold" /> All contacts</Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">// contact</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">{c.name}</h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <StageBadge stage={c.stage} />
            {c.company && <span className="overline">{c.title ? `${c.title} · ` : ""}{c.company}</span>}
            {c.event && (
              <span className="tag flex items-center gap-2" style={{ color: "var(--accent)", borderColor: "var(--accent)" }} data-testid="met-at-badge">
                <MapPin weight="bold" /> Met at {c.event.name}{c.event.date ? ` · ${new Date(c.event.date).toLocaleDateString()}` : ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={save} data-testid="save-contact-btn" className="btn-primary flex items-center gap-2"><FloppyDisk weight="bold" /> Save</button>
          <button onClick={() => { setDraftDefaultChannel("email"); setShowDraftModal(true); }} data-testid="draft-with-ai-btn" className="btn-secondary flex items-center gap-2"><Lightning weight="bold" /> Draft with AI</button>
          {c.email && <button onClick={sendEmail} data-testid="send-email-btn" className="btn-secondary flex items-center gap-2"><EnvelopeSimple weight="bold" /> Email</button>}
          {c.phone && <a href={`tel:${c.phone}`} data-testid="call-btn" className="btn-secondary flex items-center gap-2"><Phone weight="bold" /> Call</a>}
          <button onClick={archiveContact} data-testid="archive-contact-btn" className="btn-secondary flex items-center gap-2"><Archive weight="bold" /> Archive</button>
          <button onClick={() => setShowCampaignPicker(true)} data-testid="add-to-campaign-btn" className="btn-secondary flex items-center gap-2"><Lightning weight="bold" /> Add to Campaign</button>
          <button onClick={remove} data-testid="delete-contact-btn" className="btn-secondary flex items-center gap-2"><Trash weight="bold" /> Delete</button>
        </div>
      </div>

      {/* Phase 1 — Tags row */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="tags-row">
        {(c.tags || []).map((t) => (
          <span key={t} className="tag flex items-center gap-1" data-testid={`tag-chip-${t}`}>
            {t}
            <button onClick={() => removeTag(t)} className="opacity-60 hover:opacity-100" data-testid={`remove-tag-${t}`}><X weight="bold" /></button>
          </span>
        ))}
        <div className="flex items-center gap-2">
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTag()}
            placeholder="+ add tag"
            className="input-base"
            style={{ width: 140, padding: "4px 10px", fontSize: 12 }}
            data-testid="tag-input"
          />
          {tagInput && (
            <button onClick={addTag} className="btn-secondary text-xs px-2 py-1" data-testid="tag-add-btn"><Plus weight="bold" /></button>
          )}
        </div>
      </div>

      {/* Phase 1 — Relationship + Priority panel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t mb-8" style={{ borderColor: "var(--border)" }} data-testid="relationship-panel">
        <PanelCell label="Relationship">
          <select
            value={edit.relationship_type || ""}
            onChange={(e) => setEdit({ ...edit, relationship_type: e.target.value || null })}
            className="input-base"
            style={{ background: "transparent", border: "none", padding: 0, fontSize: 14 }}
            data-testid="edit-relationship-type"
          >
            {RELATIONSHIP_TYPES.map((rt) => <option key={rt} value={rt}>{rt ? rt.replace("_", " ") : "— none —"}</option>)}
          </select>
        </PanelCell>
        <PanelCell label="Priority">
          <select
            value={edit.priority || "normal"}
            onChange={(e) => setEdit({ ...edit, priority: e.target.value })}
            className="input-base"
            style={{ background: "transparent", border: "none", padding: 0, fontSize: 14 }}
            data-testid="edit-priority"
          >
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </PanelCell>
        <PanelCell label="Lead score">
          <input type="number" min="0" max="100" value={edit.lead_score ?? 0}
            onChange={(e) => setEdit({ ...edit, lead_score: Number(e.target.value) })}
            className="input-base" style={{ background: "transparent", border: "none", padding: 0, fontSize: 14 }}
            data-testid="edit-lead-score" />
        </PanelCell>
        <PanelCell label="Est. value ($)">
          <input type="number" min="0" value={edit.estimated_value ?? 0}
            onChange={(e) => setEdit({ ...edit, estimated_value: Number(e.target.value) })}
            className="input-base" style={{ background: "transparent", border: "none", padding: 0, fontSize: 14 }}
            data-testid="edit-estimated-value" />
        </PanelCell>
      </div>

      {/* Phase 4 — AI Next Best Action */}
      <div className="mt-6 mb-6">
        <NextBestAction
          contactId={id}
          onDraft={(ch) => { setDraftDefaultChannel(ch || "email"); setShowDraftModal(true); }}
          onCreateTask={(text) => {
            setTaskForm((f) => ({ ...f, title: (text || "").slice(0, 160) }));
            setShowTaskForm(true);
          }}
          onScheduleMeeting={() => setShowMeetingForm(true)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Edit form */}
        <section className="lg:col-span-7">
          <div className="border p-6" style={{ borderColor: "var(--border)" }}>
            <div className="overline mb-4">// details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="First name" value={edit.first_name} onChange={(v) => setEdit({ ...edit, first_name: v })} testid="edit-first-name" />
              <Field label="Last name" value={edit.last_name} onChange={(v) => setEdit({ ...edit, last_name: v })} testid="edit-last-name" />
              <Field label="Company" value={edit.company} onChange={(v) => setEdit({ ...edit, company: v })} testid="edit-company" />
              <Field label="Title" value={edit.title} onChange={(v) => setEdit({ ...edit, title: v })} testid="edit-title" />
              <Field label="Email" value={edit.email} onChange={(v) => setEdit({ ...edit, email: v })} testid="edit-email" />
              <Field label="Secondary email" value={edit.secondary_email} onChange={(v) => setEdit({ ...edit, secondary_email: v })} testid="edit-secondary-email" />
              <Field label="Phone" value={edit.phone} onChange={(v) => setEdit({ ...edit, phone: v })} testid="edit-phone" />
              <Field label="Secondary phone" value={edit.secondary_phone} onChange={(v) => setEdit({ ...edit, secondary_phone: v })} testid="edit-secondary-phone" />
              <Field label="Website" value={edit.website} onChange={(v) => setEdit({ ...edit, website: v })} testid="edit-website" />
              <Field label="LinkedIn URL" value={edit.linkedin_url} onChange={(v) => setEdit({ ...edit, linkedin_url: v })} testid="edit-linkedin-url" />
              <Field label="Address" value={edit.address} onChange={(v) => setEdit({ ...edit, address: v })} testid="edit-address" />
              <Field label="City" value={edit.city} onChange={(v) => setEdit({ ...edit, city: v })} testid="edit-city" />
              <Field label="State" value={edit.state} onChange={(v) => setEdit({ ...edit, state: v })} testid="edit-state" />
              <Field label="Zip / Postal" value={edit.zip} onChange={(v) => setEdit({ ...edit, zip: v })} testid="edit-zip" />
              <Field label="Country" value={edit.country} onChange={(v) => setEdit({ ...edit, country: v })} testid="edit-country" />
              <Field label="Source detail" value={edit.source_detail} onChange={(v) => setEdit({ ...edit, source_detail: v })} testid="edit-source-detail" />
              <div className="md:col-span-2">
                <label className="label-base">Stage</label>
                <StageSelect value={edit.stage} onChange={(v) => setEdit({ ...edit, stage: v })} testid="edit-stage" />
              </div>
              <div className="md:col-span-2">
                <label className="label-base">Event (where you met)</label>
                <select value={edit.event_id || ""} onChange={(e) => setEdit({ ...edit, event_id: e.target.value || null })} className="input-base"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="edit-event">
                  <option value="">— no event —</option>
                  {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}{ev.date ? ` · ${new Date(ev.date).toLocaleDateString()}` : ""}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-base">Notes</label>
                <textarea data-testid="edit-notes" value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={4}
                  className="input-base" style={{ border: "1px solid var(--border)", padding: "10px 12px" }} />
              </div>
            </div>
          </div>

          {/* Phase 1 — Tabbed activity (Timeline / Notes / Log Interaction) */}
          <div className="border mt-8" style={{ borderColor: "var(--border)" }}>
            <div className="flex border-b" style={{ borderColor: "var(--border)" }} data-testid="activity-tabs">
              <TabButton active={tab === "timeline"} onClick={() => setTab("timeline")} testid="tab-timeline">
                <Clock weight="bold" /> Timeline
              </TabButton>
              <TabButton active={tab === "notes"} onClick={() => setTab("notes")} testid="tab-notes">
                <Note weight="bold" /> Notes {notes.length ? `(${notes.length})` : ""}
              </TabButton>
              <TabButton active={tab === "log"} onClick={() => setTab("log")} testid="tab-log">
                <Lightning weight="bold" /> Log activity
              </TabButton>
            </div>

            {tab === "timeline" && (
              <div className="p-6" data-testid="timeline-pane">
                {timeline.length === 0 && <div className="text-sm" style={{ color: "var(--text-2)" }}>Nothing on the timeline yet. Logging interactions or adding notes will populate this feed.</div>}
                <div className="space-y-3">
                  {timeline.map((ev) => (
                    <div key={`${ev.kind}-${ev.id}`} className="border-l-2 pl-4 py-1" style={{ borderColor: eventColor(ev.event_type) }} data-testid={`tl-${ev.kind}-${ev.id}`}>
                      <div className="flex justify-between flex-wrap gap-2">
                        <span className="tag" style={{ color: eventColor(ev.event_type), borderColor: eventColor(ev.event_type) }}>{ev.event_type.replace(".", " / ").replace("_", " ")}</span>
                        <span className="overline">{ev.occurred_at ? new Date(ev.occurred_at).toLocaleString() : ""}</span>
                      </div>
                      <div className="mt-1 text-sm font-semibold">{ev.title}</div>
                      {ev.description && <div className="mt-1 text-sm" style={{ color: "var(--text-2)" }}>{ev.description}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "notes" && (
              <div className="p-6" data-testid="notes-pane">
                <div className="flex gap-3 mb-4 flex-wrap">
                  <select
                    value={newNote.note_type}
                    onChange={(e) => setNewNote({ ...newNote, note_type: e.target.value })}
                    className="input-base"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px", width: "auto" }}
                    data-testid="new-note-type"
                  >
                    {NOTE_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                  </select>
                  <input
                    data-testid="new-note-text"
                    value={newNote.note}
                    onChange={(e) => setNewNote({ ...newNote, note: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && addNote()}
                    placeholder="Add a note…"
                    className="input-base flex-1"
                  />
                  <button onClick={addNote} data-testid="add-note-btn" className="btn-primary flex items-center gap-2"><Plus weight="bold" /> Add</button>
                </div>
                {notes.length === 0 && <div className="text-sm" style={{ color: "var(--text-2)" }}>No notes yet.</div>}
                <div className="space-y-3">
                  {notes.map((n) => (
                    <div key={n.id} className="border p-3 flex justify-between items-start gap-3" style={{ borderColor: "var(--border)" }} data-testid={`note-${n.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-2 items-center">
                          <span className="tag" style={{ color: "var(--accent)", borderColor: "var(--accent)" }}>{n.note_type?.replace("_", " ")}</span>
                          <span className="overline">{new Date(n.created_at).toLocaleString()}</span>
                        </div>
                        <div className="mt-2 text-sm whitespace-pre-wrap">{n.note}</div>
                      </div>
                      <button onClick={() => deleteNote(n.id)} className="text-xs opacity-60 hover:opacity-100" data-testid={`delete-note-${n.id}`}>
                        <Trash weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "log" && (
              <div className="p-6" data-testid="log-pane">
                <div className="flex gap-3 mb-4 flex-wrap">
                  <select value={interaction.type} onChange={(e) => setInteraction({ ...interaction, type: e.target.value })} className="input-base"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px", width: "auto" }} data-testid="interaction-type">
                    {["note", "meeting", "call", "email", "coffee", "message"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input data-testid="interaction-notes" value={interaction.notes} onChange={(e) => setInteraction({ ...interaction, notes: e.target.value })} placeholder="What happened?" className="input-base flex-1" />
                  <button onClick={addInteraction} data-testid="log-interaction-btn" className="btn-primary flex items-center gap-2"><Plus weight="bold" /> Log</button>
                </div>
                {data.interactions.length === 0 && <div className="text-sm" style={{ color: "var(--text-2)" }}>No interactions yet. Logging one records it on the timeline.</div>}
                <div className="space-y-3">
                  {data.interactions.map((it) => (
                    <div key={it.id} className="border-l-2 pl-4 py-1" style={{ borderColor: "var(--border-2)" }}>
                      <div className="flex justify-between"><span className="tag">{it.type}</span><span className="overline">{new Date(it.date).toLocaleString()}</span></div>
                      <div className="mt-2 text-sm">{it.notes}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Right column */}
        <section className="lg:col-span-5">
          {/* Phase 4 — Relationship score + AI suggestions + comm prefs */}
          <div className="space-y-4 mb-8">
            <RelationshipScore contactId={id} />
            <AISuggestions
              key={suggestionsReloadKey}
              contactId={id}
              onApplied={() => { load(); setSuggestionsReloadKey((k) => k + 1); }}
            />
            <CommPrefs contactId={id} />
            <VisibilityPicker contactId={id} />
          </div>

          {/* Follow-up */}
          <div className="border p-6" style={{ borderColor: "var(--border)" }}>
            <div className="overline mb-4">// schedule follow-up</div>
            <label className="label-base">Type</label>
            <select value={fup.type} onChange={(e) => setFup({ ...fup, type: e.target.value })} className="input-base mb-3"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="fup-type">
              {["meeting", "coffee", "call", "email", "message", "other"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="label-base">When</label>
            <input type="date" value={fup.due_date} onChange={(e) => setFup({ ...fup, due_date: e.target.value })} className="input-base mb-3"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px", colorScheme: "dark" }} data-testid="fup-date" />
            <label className="label-base">Notes</label>
            <textarea value={fup.notes} onChange={(e) => setFup({ ...fup, notes: e.target.value })} rows={2} className="input-base mb-4"
              style={{ border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="fup-notes" />
            <button onClick={addFollowup} data-testid="add-followup-btn" className="btn-primary w-full">Set reminder</button>
          </div>

          {/* Phase 2 — Meetings */}
          <div className="border mt-8 p-6" style={{ borderColor: "var(--border)" }} data-testid="meetings-section">
            <div className="flex items-center justify-between mb-4">
              <div className="overline flex items-center gap-2"><CalendarCheck weight="bold" /> // meetings</div>
              <button onClick={() => setShowMeetingForm(!showMeetingForm)} data-testid="toggle-meeting-form" className="btn-secondary text-xs px-2 py-1 flex items-center gap-1">
                <Plus weight="bold" /> New
              </button>
            </div>
            {showMeetingForm && (
              <div className="mb-4 space-y-2" data-testid="meeting-form">
                <input placeholder="Title (e.g. Coffee chat)" value={meetingForm.title}
                  onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                  className="input-base" data-testid="meeting-title" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="datetime-local" value={meetingForm.start_time}
                    onChange={(e) => setMeetingForm({ ...meetingForm, start_time: e.target.value })}
                    className="input-base" style={{ colorScheme: "dark" }} data-testid="meeting-start" />
                  <input type="datetime-local" value={meetingForm.end_time}
                    onChange={(e) => setMeetingForm({ ...meetingForm, end_time: e.target.value })}
                    className="input-base" style={{ colorScheme: "dark" }} data-testid="meeting-end" />
                </div>
                <input placeholder="Location or video URL" value={meetingForm.location}
                  onChange={(e) => setMeetingForm({ ...meetingForm, location: e.target.value })}
                  className="input-base" data-testid="meeting-location" />
                <button onClick={createMeeting} className="btn-primary w-full" data-testid="create-meeting-btn">Schedule</button>
              </div>
            )}
            {meetings.length === 0 && !showMeetingForm && (
              <div className="text-sm" style={{ color: "var(--text-2)" }}>No meetings yet.</div>
            )}
            <div className="space-y-2">
              {meetings.map((m) => (
                <div key={m.id} className="border p-3" style={{ borderColor: "var(--border)" }} data-testid={`meeting-${m.id}`}>
                  <div className="flex justify-between gap-2 flex-wrap">
                    <div className="font-semibold text-sm">{m.title}</div>
                    <span className="tag" style={{ color: statusColor(m.status), borderColor: statusColor(m.status) }}>{m.status}</span>
                  </div>
                  <div className="overline mt-1">{new Date(m.start_time).toLocaleString()}</div>
                  {m.location && <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>@ {m.location}</div>}
                  {m.provider_status === "local_only" && (
                    <div className="tag mt-2" style={{ color: "#FF9500", borderColor: "#FF9500" }} title="This meeting is not on an external calendar">LOCAL ONLY</div>
                  )}
                  {m.status === "scheduled" && (
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => completeMeeting(m)} className="btn-secondary text-xs px-2 py-1" data-testid={`complete-meeting-${m.id}`}>Mark done</button>
                      <button onClick={() => cancelMeeting(m)} className="btn-secondary text-xs px-2 py-1" data-testid={`cancel-meeting-${m.id}`}>Cancel</button>
                    </div>
                  )}
                  {m.outcome_notes && <div className="text-xs mt-2" style={{ color: "var(--text-3)" }}>“{m.outcome_notes}”</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Phase 2 — Tasks */}
          <div className="border mt-8 p-6" style={{ borderColor: "var(--border)" }} data-testid="tasks-section">
            <div className="flex items-center justify-between mb-4">
              <div className="overline flex items-center gap-2"><CheckSquare weight="bold" /> // tasks</div>
              <button onClick={() => setShowTaskForm(!showTaskForm)} data-testid="toggle-task-form" className="btn-secondary text-xs px-2 py-1 flex items-center gap-1">
                <Plus weight="bold" /> New
              </button>
            </div>
            {showTaskForm && (
              <div className="mb-4 space-y-2" data-testid="task-form-inline">
                <input placeholder="Title" value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="input-base" data-testid="task-title-inline" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={taskForm.due_at}
                    onChange={(e) => setTaskForm({ ...taskForm, due_at: e.target.value })}
                    className="input-base" style={{ colorScheme: "dark" }} data-testid="task-due-inline" />
                  <select value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }}
                    data-testid="task-priority-inline"
                  >
                    {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <button onClick={createTask} className="btn-primary w-full" data-testid="create-task-btn">Add task</button>
              </div>
            )}
            {tasks.length === 0 && !showTaskForm && (
              <div className="text-sm" style={{ color: "var(--text-2)" }}>No tasks for this contact.</div>
            )}
            <div className="space-y-2">
              {tasks.map((t) => (
                <div key={t.id} className="border p-3 flex items-center gap-2" style={{ borderColor: "var(--border)" }} data-testid={`task-${t.id}`}>
                  <button onClick={() => toggleTaskComplete(t)} data-testid={`toggle-task-inline-${t.id}`} aria-label="Complete">
                    {t.status === "completed" ? (
                      <CheckCircle weight="fill" size={20} style={{ color: "var(--accent)" }} />
                    ) : (
                      <div style={{ width: 20, height: 20, border: "2px solid var(--border-2)", borderRadius: "50%" }} />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${t.status === "completed" ? "line-through opacity-60" : ""}`}>{t.title}</div>
                    {t.due_at && <div className="overline mt-1">Due {new Date(t.due_at).toLocaleDateString()}</div>}
                  </div>
                  <span className="tag" style={{ color: priorityColor(t.priority), borderColor: priorityColor(t.priority) }}>{t.priority}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 3 — Active campaign enrollments */}
          <div className="border mt-8 p-6" style={{ borderColor: "var(--border)" }} data-testid="contact-campaigns-panel">
            <div className="overline mb-4 flex items-center gap-2"><Lightning weight="bold" style={{ color: "var(--accent)" }} /> // automation</div>
            {contactEnrollments.length === 0 ? (
              <div className="text-sm" style={{ color: "var(--text-2)" }}>
                Not enrolled in any automation yet. <button onClick={() => setShowCampaignPicker(true)} className="underline" data-testid="panel-open-picker">Add to a campaign</button>.
              </div>
            ) : (
              <div className="space-y-2">
                {contactEnrollments.map((e) => {
                  const camp = e.campaign || {};
                  const statusColorMap = { active: "var(--accent)", paused: "#FF9500", completed: "#34C759", exited: "var(--text-3)", bounced: "#FF3B30" };
                  const col = statusColorMap[e.status] || "var(--text-3)";
                  return (
                    <div key={e.id} className="border p-3 flex items-center gap-3" style={{ borderColor: "var(--border)" }} data-testid={`enrollment-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <Link to={`/campaigns/${e.campaign_id}`} className="font-semibold hover:underline block truncate">{camp.name || "Campaign"}</Link>
                        <div className="overline mt-1">
                          {typeof e.step_index === "number"
                            ? `step ${e.step_index + 1}`
                            : (e.status === "completed" ? "finished" : "in progress")}
                          {e.next_action_at ? ` · next ${new Date(e.next_action_at).toLocaleString()}` : ""}
                        </div>
                      </div>
                      <span className="tag" style={{ color: col, borderColor: col }}>{e.status}</span>
                      <div className="flex gap-1">
                        {e.status === "active" && (
                          <button onClick={() => pauseEnrollment(e.id)} className="btn-secondary text-xs px-2" data-testid={`pause-enrollment-${e.id}`}>Pause</button>
                        )}
                        {e.status === "paused" && (
                          <button onClick={() => resumeEnrollment(e.id)} className="btn-secondary text-xs px-2" data-testid={`resume-enrollment-${e.id}`}>Resume</button>
                        )}
                        <button onClick={() => removeEnrollment(e.id)} className="btn-secondary text-xs px-2" data-testid={`remove-enrollment-${e.id}`}><Trash weight="bold" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Campaigns (legacy sequences) */}
          {campaigns.length > 0 && (
            <div className="border mt-8 p-6" style={{ borderColor: "var(--border)" }}>
              <div className="overline mb-4">// apply campaign</div>
              <div className="space-y-2">
                {campaigns.map((cp) => (
                  <button key={cp.id} onClick={() => applyCampaign(cp.id)} data-testid={`apply-campaign-${cp.id}`}
                    className="w-full text-left p-3 border hover:bg-[#15303A] flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
                    <div>
                      <div className="font-semibold">{cp.name}</div>
                      <div className="overline">{cp.steps?.length || 0} steps</div>
                    </div>
                    <PaperPlaneTilt weight="bold" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Templates quick send */}
          <div className="border mt-8 p-6" style={{ borderColor: "var(--border)" }}>
            <div className="overline mb-4">// send template</div>
            {templates.length === 0 && <div className="text-sm text-[#C7DDE5]">No templates yet. <Link to="/templates" className="underline">Create one</Link></div>}
            <div className="space-y-2">
              {templates.slice(0, 6).map((t) => (
                <button key={t.id} onClick={() => fillFromTemplate(t)} data-testid={`use-template-${t.id}`}
                  className="w-full text-left p-3 border hover:bg-[#15303A] flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
                  <div>
                    <div className="font-semibold">{t.name}</div>
                    <div className="overline">{t.category}</div>
                  </div>
                  <EnvelopeSimple weight="bold" />
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Phase 3 — Campaign picker modal */}
      {showCampaignPicker && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-50"
          style={{ background: "rgba(2,12,15,0.78)" }}
          onClick={() => setShowCampaignPicker(false)}
          data-testid="campaign-picker-modal"
        >
          <div
            className="border p-6 w-full max-w-lg"
            style={{ borderColor: "var(--border)", background: "var(--surface)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="overline flex items-center gap-2"><Lightning weight="bold" style={{ color: "var(--accent)" }} /> // enroll in automation</div>
              <button onClick={() => setShowCampaignPicker(false)} data-testid="close-picker" className="btn-secondary text-xs px-2"><X weight="bold" /></button>
            </div>
            {availableCampaigns.length === 0 ? (
              <div className="text-sm py-6 text-center" style={{ color: "var(--text-2)" }}>
                No active campaigns yet. <Link to="/campaigns" className="underline" data-testid="picker-go-to-campaigns">Create one in Campaigns</Link>.
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-auto">
                {availableCampaigns.map((cp) => {
                  const already = contactEnrollments.some((e) => e.campaign_id === cp.id && ["active","paused"].includes(e.status));
                  return (
                    <button
                      key={cp.id}
                      disabled={already}
                      onClick={() => enrollInCampaign(cp.id)}
                      data-testid={`enroll-campaign-${cp.id}`}
                      className="w-full text-left p-3 border hover:bg-[#15303A] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-3"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{cp.name}</div>
                        <div className="overline mt-1">{cp.step_count || 0} steps · trigger: {cp.trigger_type || "manual"}</div>
                      </div>
                      {already ? (
                        <span className="tag" style={{ color: "var(--text-3)", borderColor: "var(--text-3)" }}>enrolled</span>
                      ) : (
                        <PaperPlaneTilt weight="bold" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Phase 4 — Draft message modal */}
      <DraftMessageModal
        open={showDraftModal}
        onClose={() => setShowDraftModal(false)}
        contactId={id}
        defaultChannel={draftDefaultChannel}
      />
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-base">{label}</label>
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-base" />
    </div>
  );
}

function PanelCell({ label, children }) {
  return (
    <div className="border-r border-b p-4" style={{ borderColor: "var(--border)" }}>
      <div className="overline mb-1">{label}</div>
      <div style={{ color: "var(--text)" }}>{children}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-5 py-3 flex items-center gap-2 text-sm font-mono uppercase tracking-wider border-r"
      style={{
        borderColor: "var(--border)",
        background: active ? "var(--surface-2)" : "transparent",
        color: active ? "var(--accent)" : "var(--text-2)",
        borderBottom: active ? "2px solid var(--accent)" : "none",
      }}
    >
      {children}
    </button>
  );
}

function eventColor(evType = "") {
  if (evType.startsWith("stage")) return "#FF9500";
  if (evType.startsWith("note")) return "#2DD4D4";
  if (evType.includes("email_sent")) return "#34C759";
  if (evType.includes("email_received")) return "#4FC3FF";
  if (evType.includes("meeting_scheduled")) return "#AF52DE";
  if (evType.includes("meeting_completed")) return "#34C759";
  if (evType.includes("meeting_cancelled")) return "#FF3B30";
  if (evType.includes("meeting_no_show")) return "#FF9500";
  if (evType.includes("task_created")) return "#5AC8FA";
  if (evType.includes("task_completed")) return "#34C759";
  if (evType.includes("merged")) return "#AF52DE";
  if (evType.includes("contact_created")) return "var(--accent)";
  if (evType.includes("interaction")) return "var(--text-3)";
  return "var(--text-3)";
}

function statusColor(s) {
  return {
    scheduled: "var(--accent)",
    completed: "#34C759",
    cancelled: "#FF3B30",
    rescheduled: "#FF9500",
    no_show: "#FF9500",
  }[s] || "var(--text-3)";
}

function priorityColor(p) {
  return { low: "var(--text-3)", normal: "var(--text-2)", high: "#FF9500", urgent: "#FF3B30" }[p] || "var(--text-3)";
}
